import React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { Pencil, LockKeyhole, Trash2 } from "lucide-react";
import { Alert } from "components/ui/alert";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { AccessControlGroupResponse, AccessControlGroupType } from "api/ApiClient";
import axios from "axios";
import { BASE_API_URL } from "store/Me";

export type AdminAccessControlGroup = AccessControlGroupResponse & {
  siteId?: string;
  systemGroupId?: string;
};

type Props = {
  group: AdminAccessControlGroup;
  siteName?: string;
  onUpdated: () => void;
  onDeleteClick: () => void;
};

export default function GroupDetails({ group, siteName, onUpdated, onDeleteClick }: Props) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <section className="overflow-hidden bg-white border rounded-lg shadow-sm border-slate-200">
        <header className="flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-200">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              Group details
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              Configuration and membership
            </p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label={`Edit ${group.name ?? group.id}`}
                  className="text-primary hover:text-primary"
                  onClick={() => setOpen(true)}
                  size="icon-sm"
                  type="button"
                  variant="ghost"
                >
                  <Pencil />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Edit group</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label={`Delete ${group.name ?? group.id}`}
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={onDeleteClick}
                  size="icon-sm"
                  type="button"
                  variant="ghost"
                >
                  <Trash2 />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete group</TooltipContent>
            </Tooltip>
          </div>
        </header>
        <dl className="divide-y divide-slate-100">
          <GroupDetailItem label="Name">{group.name ?? "—"}</GroupDetailItem>
          <GroupDetailItem label="Type">
            <Badge variant="secondary">{formatGroupType(group.type)}</Badge>
          </GroupDetailItem>
          {group.description && (
            <GroupDetailItem label="Description">{group.description}</GroupDetailItem>
          )}
          {group.externalId && (
            <GroupDetailItem label="External ID" mono>
              {group.externalId}
            </GroupDetailItem>
          )}
          {group.systemGroupId && (
            <GroupDetailItem label="System Group ID" mono>
              {group.systemGroupId}
            </GroupDetailItem>
          )}
          {group.propertyExternalId && (
            <GroupDetailItem label="Property External ID">
              {group.propertyExternalId}
            </GroupDetailItem>
          )}
          {group.unitNumber && (
            <GroupDetailItem label="Unit Number">{group.unitNumber}</GroupDetailItem>
          )}
          <GroupDetailItem label="No Tour Enabled">
            {group.noTourEnabled ? "Yes" : "No"}
          </GroupDetailItem>
          {group.siteId && (
            <GroupDetailItem label="Site">
              <span className="inline-flex items-center gap-1.5">
                <LockKeyhole className="size-4 text-slate-400" />
                {siteName || group.siteId}
              </span>
            </GroupDetailItem>
          )}
        </dl>
      </section>

      <EditGroupModal
        open={open}
        onClose={() => setOpen(false)}
        group={group}
        onUpdated={onUpdated}
      />
    </>
  );
}

function GroupDetailItem({
  label,
  mono,
  children,
}: {
  label: string;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] gap-4 px-4 py-3">
      <dt className="text-xs font-semibold tracking-wide uppercase text-slate-500">
        {label}
      </dt>
      <dd
        className={`min-w-0 text-sm font-medium wrap-break-word text-slate-800 ${mono ? "font-mono" : ""}`}
      >
        {children}
      </dd>
    </div>
  );
}

function EditGroupModal({
  open,
  onClose,
  group,
  onUpdated,
}: {
  open: boolean;
  onClose: () => void;
  group: AdminAccessControlGroup;
  onUpdated: () => void;
}) {
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState({
    name: group.name ?? "",
    description: group.description ?? "",
    externalId: group.externalId ?? "",
    propertyExternalId: group.propertyExternalId ?? "",
    unitNumber: group.unitNumber ?? "",
    type: group.type ?? AccessControlGroupType.Regular,
    noTourEnabled: !!group.noTourEnabled,
  });

  React.useEffect(() => {
    if (!open) {
      return;
    }

    setForm({
      name: group.name ?? "",
      description: group.description ?? "",
      externalId: group.externalId ?? "",
      propertyExternalId: group.propertyExternalId ?? "",
      unitNumber: group.unitNumber ?? "",
      type: group.type ?? AccessControlGroupType.Regular,
      noTourEnabled: !!group.noTourEnabled,
    });
    setError(null);
  }, [group, open]);

  const submit = async () => {
    if (!group.id) {
      setError("Group id is missing.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await axios.patch(`${BASE_API_URL}/api/AccessControlGroups/${group.id}/info`, {
        name: form.name,
        description: form.description,
        externalId: form.externalId,
        propertyExternalId: form.propertyExternalId || null,
        unitNumber: form.unitNumber || null,
        type: form.type,
        noTourEnabled: form.noTourEnabled,
      });

      onUpdated();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Unable to update group.");
    } finally {
      setSaving(false);
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !saving) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="sm:max-w-lg"
        onEscapeKeyDown={(event) => {
          if (saving) {
            event.preventDefault();
          }
        }}
        onPointerDownOutside={(event) => {
          if (saving) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit Access Control Group</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          {error ? <Alert variant="destructive">{error}</Alert> : null}
          <div className="grid gap-2">
            <Label htmlFor="group-edit-name">Name</Label>
            <Input
              id="group-edit-name"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="group-edit-description">Description</Label>
            <Input
              id="group-edit-description"
              value={form.description}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, description: event.target.value }))
              }
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="group-edit-external-id">External ID</Label>
            <Input
              id="group-edit-external-id"
              value={form.externalId}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, externalId: event.target.value }))
              }
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="group-edit-property-external-id">Property External ID</Label>
            <Input
              id="group-edit-property-external-id"
              value={form.propertyExternalId}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, propertyExternalId: event.target.value }))
              }
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="group-edit-unit-number">Unit Number</Label>
            <Input
              id="group-edit-unit-number"
              value={form.unitNumber}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, unitNumber: event.target.value }))
              }
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="group-edit-type">Type</Label>
            <Select
              value={form.type}
              onValueChange={(type) =>
                setForm((prev) => ({
                  ...prev,
                  type: type as AccessControlGroupType,
                }))
              }
            >
              <SelectTrigger id="group-edit-type" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(AccessControlGroupType).map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="group-edit-no-tour-enabled"
              checked={form.noTourEnabled}
              onCheckedChange={(checked) =>
                setForm((prev) => ({ ...prev, noTourEnabled: checked === true }))
              }
            />
            <Label htmlFor="group-edit-no-tour-enabled">No Tour Enabled</Label>
          </div>
        </div>
        <DialogFooter>
          <Button disabled={saving} onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button disabled={saving} onClick={submit} type="button">
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function formatGroupType(type: string | undefined): string {
  if (!type) {
    return "-";
  }

  return type
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (value) => value.toUpperCase())
    .trim();
}
